import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCrmData } from '../../contexts/CrmDataContext';
import {
  DAILY_VISIT_TARGET,
  HIGH_FREQ_VISIT_THRESHOLD,
  PROJECT_UPDATE_ON_TIME_DAYS,
  type CrmWeeklySummaryReport,
  type WeeklyVisitRow,
} from '../../lib/crmWeeklySummary';
import {
  buildLocalWeeklyNarrative,
  fetchCrmWeeklyAiNarrative,
  type CrmWeeklyAiNarrative,
} from '../../lib/crmWeeklySummaryAi';
import {
  fetchDashboardMonthlySales,
  formatSalesAmountFull,
  formatSalesAmountShort,
  monthShortLabel,
  type DashboardMonthlySales,
} from '../../lib/dashboardMonthlySales';
import { formatMonthKeyLabel } from '../../lib/accountAnalytics';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import {
  TASK_TITLE_CHART_COLORS,
  buildTaskTitlePieSlicesFromTasks,
  categorizeTaskTitle,
} from '../../utils/taskHelpers';

type ChartScope = 'overall' | 'salesperson';

const NEW_VISIT_COLOR = TASK_TITLE_CHART_COLORS['New Visit'] ?? '#E9B44C';
const OTHER_VISIT_COLOR = '#3d6ea8';

function ChartScopeToggle({
  value,
  onChange,
  showSalesperson,
}: {
  value: ChartScope;
  onChange: (next: ChartScope) => void;
  showSalesperson: boolean;
}) {
  return (
    <div className="analytics-page__coverage-filter" role="group" aria-label="Chart scope">
      <button
        type="button"
        className={`analytics-page__segment-btn${value === 'overall' ? ' is-active' : ''}`}
        onClick={() => onChange('overall')}
      >
        Overall
      </button>
      {showSalesperson ? (
        <button
          type="button"
          className={`analytics-page__segment-btn${value === 'salesperson' ? ' is-active' : ''}`}
          onClick={() => onChange('salesperson')}
        >
          By salesperson
        </button>
      ) : null}
    </div>
  );
}

function SalespersonPicker({
  options,
  value,
  onChange,
}: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (key: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <label className="analytics-weekly__person-pick">
      <span className="field-label">Salesperson</span>
      <select
        className="field-input field-input--compact"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

type Props = {
  report: CrmWeeklySummaryReport;
  search: string;
  onOpenAccount: (clientId: string) => void;
  onOpenTask: (taskId: string) => void;
};

function Scorecard({ report }: { report: CrmWeeklySummaryReport }) {
  const top = report.titleSlices[0];
  const nv = report.newVisitsMonth;
  const health = report.projectUpdateHealth;
  const newVisitCard = report.isTeamScope
    ? {
        label: 'Staff hitting New Visit 10',
        value: `${nv.staffMet}/${nv.staffCount}`,
        meta: `Each person needs ${nv.target} unique accounts (${nv.monthLabel})`,
      }
    : {
        label: 'New Visit (month to date)',
        value: `${nv.uniqueCount}/${nv.target}`,
        meta: nv.met ? 'Personal target met' : `${nv.remaining} still needed (personal)`,
      };
  const dailyCard = report.isTeamScope
    ? {
        label: 'Staff meeting 8 visits daily',
        value: `${report.staffDailyTargetMet}/${report.staffCount}`,
        meta: `Personal target ${report.dailyTarget} each weekday — not a team total`,
      }
    : {
        label: 'Weekdays meeting 8 visits',
        value: `${report.workdaysMet}/${report.workdayCount}`,
        meta: `Personal target ${report.dailyTarget} visits each weekday`,
      };
  const cards = [
    { label: 'Visits', value: String(report.visitCount) },
    { label: 'Accounts', value: String(report.accountCount) },
    {
      label: 'Top task type',
      value: top ? `${Math.round(top.pct)}%` : '—',
      meta: top ? top.label : undefined,
    },
    newVisitCard,
    dailyCard,
    {
      label: 'Projects',
      value: String(report.projects.total),
      meta: `${report.projects.ongoing} ongoing · ${report.projects.onHold} on hold · ${report.projects.completed} completed`,
    },
    {
      label: 'Project updates on time',
      value: health.ongoingTotal === 0 ? '—' : `${health.onTime}/${health.ongoingTotal}`,
      meta: `Within ${PROJECT_UPDATE_ON_TIME_DAYS} days · ${health.stale + health.neverUpdated} overdue`,
    },
    { label: 'Not visited (3 months)', value: String(report.notVisitedCount) },
  ];
  if (report.overdueOpenTasks > 0) {
    cards.push({ label: 'Overdue open tasks', value: String(report.overdueOpenTasks) });
  }
  return (
    <div className="analytics-weekly__scorecard" role="list" aria-label="Weekly scorecard">
      {cards.map((card) => (
        <div key={card.label} className="analytics-weekly__score" role="listitem">
          <span className="analytics-weekly__score-value">{card.value}</span>
          <span className="analytics-weekly__score-label">{card.label}</span>
          {'meta' in card && card.meta ? (
            <span className="analytics-weekly__score-meta">{card.meta}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function isNewVisitTitle(title: string): boolean {
  return categorizeTaskTitle(title) === 'New Visit';
}

function DayBarChart({ report }: { report: CrmWeeklySummaryReport }) {
  const personOptions = useMemo(
    () =>
      report.byOwner.map((o) => ({
        key: o.ownerId || o.ownerLabel,
        label: o.ownerLabel,
      })),
    [report.byOwner],
  );
  const showSalesperson = personOptions.length > 1;
  const [scope, setScope] = useState<ChartScope>('overall');
  const [personKey, setPersonKey] = useState(personOptions[0]?.key ?? '');

  useEffect(() => {
    if (!showSalesperson && scope === 'salesperson') setScope('overall');
  }, [showSalesperson, scope]);

  useEffect(() => {
    if (personOptions.length === 0) return;
    if (!personOptions.some((o) => o.key === personKey)) {
      setPersonKey(personOptions[0].key);
    }
  }, [personOptions, personKey]);

  const weekdays = useMemo(() => report.byDay.filter((d) => d.isWorkday), [report.byDay]);

  const dayStacks = useMemo(() => {
    const visits =
      scope === 'salesperson' && personKey
        ? report.visits.filter((v) => (v.ownerId || v.ownerLabel) === personKey)
        : report.visits;
    return weekdays.map((day) => {
      const dayVisits = visits.filter((v) => v.dayKey === day.dayKey);
      const newCount = dayVisits.filter((v) => isNewVisitTitle(v.title)).length;
      const otherCount = dayVisits.length - newCount;
      const total = dayVisits.length;
      const personalMiss =
        scope === 'salesperson' || !report.isTeamScope
          ? total < DAILY_VISIT_TARGET
          : false;
      return {
        ...day,
        newCount,
        otherCount,
        total,
        personalMiss,
      };
    });
  }, [weekdays, report.visits, report.isTeamScope, scope, personKey]);

  const max = Math.max(DAILY_VISIT_TARGET, ...dayStacks.map((d) => d.total), 1);
  const personLabel =
    scope === 'salesperson'
      ? personOptions.find((o) => o.key === personKey)?.label ?? 'Salesperson'
      : null;

  return (
    <section className="card analytics-weekly__chart" aria-label="Visits by day">
      <header className="analytics-weekly__chart-head analytics-weekly__chart-head--stack">
        <div>
          <h3 className="analytics-weekly__chart-title">Visits by day</h3>
          <p className="analytics-weekly__chart-meta">
            {scope === 'salesperson' && personLabel
              ? `${personLabel}: personal target ${DAILY_VISIT_TARGET} visits each weekday (Monday–Friday)`
              : report.isTeamScope
                ? `Team volume Monday–Friday (New Visit highlighted). Personal ${DAILY_VISIT_TARGET}/day targets are per staff.`
                : `Personal target ${DAILY_VISIT_TARGET} visits each weekday · ${report.workdaysMet} of ${report.workdayCount} weekdays met`}
          </p>
        </div>
        <ChartScopeToggle
          value={scope}
          onChange={setScope}
          showSalesperson={showSalesperson}
        />
      </header>
      {scope === 'salesperson' ? (
        <SalespersonPicker options={personOptions} value={personKey} onChange={setPersonKey} />
      ) : null}
      <div className="analytics-weekly__legend" aria-hidden>
        <span>
          <i className="analytics-weekly__swatch" style={{ background: OTHER_VISIT_COLOR }} /> Other
          visits
        </span>
        <span>
          <i className="analytics-weekly__swatch" style={{ background: NEW_VISIT_COLOR }} /> New Visit
        </span>
      </div>
      <div
        className="analytics-weekly__bars analytics-weekly__bars--weekdays"
        role="img"
        aria-label="Weekday visit counts"
      >
        {dayStacks.map((day) => {
          const otherPct =
            day.otherCount === 0 ? 0 : Math.max(8, Math.round((day.otherCount / max) * 100));
          const newPct =
            day.newCount === 0 ? 0 : Math.max(8, Math.round((day.newCount / max) * 100));
          return (
            <div key={day.dayKey} className="analytics-weekly__bar-col">
              <span className="analytics-weekly__bar-value">{day.total || ''}</span>
              <div
                className={`analytics-weekly__bar-track${day.personalMiss && day.total > 0 ? ' is-miss-track' : ''}`}
              >
                {day.otherCount > 0 ? (
                  <div
                    className="analytics-weekly__bar-fill analytics-weekly__bar-fill--other"
                    style={{ height: `${otherPct}%`, background: OTHER_VISIT_COLOR }}
                    title={`Other visits: ${day.otherCount}`}
                  />
                ) : null}
                {day.newCount > 0 ? (
                  <div
                    className="analytics-weekly__bar-fill analytics-weekly__bar-fill--new"
                    style={{ height: `${newPct}%`, background: NEW_VISIT_COLOR }}
                    title={`New Visit: ${day.newCount}`}
                  />
                ) : null}
              </div>
              <span className="analytics-weekly__bar-label" title={day.dayLabel}>
                {day.weekdayShort}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TitleMixBars({ report }: { report: CrmWeeklySummaryReport }) {
  const personOptions = useMemo(
    () =>
      report.byOwner.map((o) => ({
        key: o.ownerId || o.ownerLabel,
        label: o.ownerLabel,
      })),
    [report.byOwner],
  );
  const showSalesperson = personOptions.length > 1;
  const [scope, setScope] = useState<ChartScope>('overall');
  const [personKey, setPersonKey] = useState(personOptions[0]?.key ?? '');

  useEffect(() => {
    if (!showSalesperson && scope === 'salesperson') setScope('overall');
  }, [showSalesperson, scope]);

  useEffect(() => {
    if (personOptions.length === 0) return;
    if (!personOptions.some((o) => o.key === personKey)) {
      setPersonKey(personOptions[0].key);
    }
  }, [personOptions, personKey]);

  const slices = useMemo(() => {
    if (scope === 'overall') return report.titleSlices.slice(0, 8);
    const filtered = report.visits.filter((v) => (v.ownerId || v.ownerLabel) === personKey);
    return buildTaskTitlePieSlicesFromTasks(filtered.map((v) => ({ title: v.title }))).slice(0, 8);
  }, [scope, report.titleSlices, report.visits, personKey]);

  const total = slices.reduce((sum, s) => sum + s.count, 0);
  const personLabel =
    scope === 'salesperson'
      ? personOptions.find((o) => o.key === personKey)?.label ?? 'Salesperson'
      : null;

  return (
    <section className="card analytics-weekly__chart analytics-weekly__chart--mix" aria-label="Task mix by title">
      <header className="analytics-weekly__chart-head analytics-weekly__chart-head--stack">
        <div>
          <h3 className="analytics-weekly__chart-title">Task mix</h3>
          <p className="analytics-weekly__chart-meta">
            {scope === 'salesperson' && personLabel
              ? `${personLabel}: ${total} visit${total === 1 ? '' : 's'} by title`
              : `Percent of visits by title (${total} total)`}
          </p>
        </div>
        <ChartScopeToggle
          value={scope}
          onChange={setScope}
          showSalesperson={showSalesperson}
        />
      </header>
      {scope === 'salesperson' ? (
        <SalespersonPicker options={personOptions} value={personKey} onChange={setPersonKey} />
      ) : null}
      {slices.length === 0 ? (
        <p className="analytics-weekly__empty">No visits this week.</p>
      ) : (
        <div className="analytics-weekly__notes-bars">
          {slices.map((slice) => (
            <div key={slice.label} className="analytics-weekly__notes-row">
              <span title={slice.label}>{slice.label}</span>
              <div className="analytics-weekly__notes-track">
                <div
                  className="analytics-weekly__notes-fill is-written"
                  style={{
                    width: `${Math.max(slice.pct, slice.count > 0 ? 4 : 0)}%`,
                    background: slice.color,
                  }}
                />
              </div>
              <span className="mono">{Math.round(slice.pct)}%</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SalesTrendCard({
  data,
  loading,
  error,
}: {
  data: DashboardMonthlySales | null;
  loading: boolean;
  error: string | null;
}) {
  const maxThis = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.months.map((m) => Math.abs(m.this_year)));
  }, [data]);
  const maxLast = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.months.map((m) => Math.abs(m.last_year)));
  }, [data]);
  const totals = useMemo(() => {
    if (!data) return { thisYear: 0, lastYear: 0 };
    return {
      thisYear: data.months.reduce((s, m) => s + m.this_year, 0),
      lastYear: data.months.reduce((s, m) => s + m.last_year, 0),
    };
  }, [data]);

  return (
    <section className="card analytics-weekly__sales" aria-label="Annual sales by month">
      <header className="analytics-weekly__chart-head">
        <h3 className="analytics-weekly__chart-title">Annual sales by month</h3>
        {data && !loading ? (
          <p className="analytics-weekly__chart-meta">
            {data.year}: {formatSalesAmountFull(totals.thisYear)} · {data.last_year}:{' '}
            {formatSalesAmountFull(totals.lastYear)}
          </p>
        ) : null}
      </header>
      {loading ? <p className="analytics-weekly__empty">Loading sales…</p> : null}
      {error ? <p className="analytics-weekly__empty">{error}</p> : null}
      {!loading && !error && data ? (
        <div className="dashboard-sales__chart analytics-weekly__sales-chart" role="list">
          {data.months.map((month) => {
            const thisPct = Math.max(
              month.this_year === 0 ? 0 : 3,
              Math.round((Math.abs(month.this_year) / maxThis) * 100),
            );
            const lastPct = Math.max(
              month.last_year === 0 ? 0 : 3,
              Math.round((Math.abs(month.last_year) / maxLast) * 100),
            );
            return (
              <div key={month.month} className="dashboard-sales__col" role="listitem">
                <div className="dashboard-sales__values">
                  <span className="dashboard-sales__value dashboard-sales__value--this">
                    {formatSalesAmountShort(month.this_year)}
                  </span>
                  <span className="dashboard-sales__value dashboard-sales__value--last">
                    {formatSalesAmountShort(month.last_year)}
                  </span>
                </div>
                <div className="dashboard-sales__bars" aria-hidden>
                  <span
                    className="dashboard-sales__bar dashboard-sales__bar--this"
                    style={{ height: `${thisPct}%` }}
                  />
                  <span
                    className="dashboard-sales__bar dashboard-sales__bar--last"
                    style={{ height: `${lastPct}%` }}
                  />
                </div>
                <span className="dashboard-sales__label">{monthShortLabel(month.month)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function VisitRowButton({
  row,
  onOpenTask,
  onOpenAccount,
}: {
  row: WeeklyVisitRow;
  onOpenTask: (taskId: string) => void;
  onOpenAccount: (clientId: string) => void;
}) {
  return (
    <li className="analytics-weekly__visit">
      <button type="button" className="analytics-weekly__visit-main" onClick={() => onOpenTask(row.taskId)}>
        <span className="analytics-weekly__visit-day">{row.dayLabel}</span>
        <span className="analytics-weekly__visit-account">
          <span className="analytics-weekly__visit-name">{row.companyName}</span>
          <span className="analytics-weekly__visit-meta">
            {row.title}
            {row.code !== '—' ? ` · ${row.code}` : ''}
            {row.ownerLabel ? ` · ${row.ownerLabel}` : ''}
          </span>
        </span>
      </button>
      {row.hasDescription ? (
        <p className="analytics-weekly__visit-desc">{row.description}</p>
      ) : null}
      {row.clientId ? (
        <button
          type="button"
          className="analytics-weekly__visit-link"
          onClick={() => onOpenAccount(row.clientId!)}
        >
          Account history
        </button>
      ) : null}
    </li>
  );
}

function OwnerTitleHint({
  report,
  ownerKey,
}: {
  report: CrmWeeklySummaryReport;
  ownerKey: string;
}) {
  const counts = new Map<string, number>();
  for (const v of report.visits) {
    if ((v.ownerId || v.ownerLabel) !== ownerKey) continue;
    const t = v.title || 'Others';
    counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2);
  if (top.length === 0) return <span className="analytics-weekly__visit-meta">—</span>;
  return (
    <span className="analytics-weekly__visit-meta">
      {top.map(([label, n]) => `${label} ${n}`).join(' · ')}
    </span>
  );
}

export function AnalyticsWeeklySummary({ report, search, onOpenAccount, onOpenTask }: Props) {
  const {
    username,
    actorSalespersonCodes,
    salespersonCodesByUsername,
    userFilter,
    canUseUserFilter,
    ownerLabel,
  } = useCrmData();

  const [ai, setAi] = useState<CrmWeeklyAiNarrative | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [sales, setSales] = useState<DashboardMonthlySales | null>(null);
  const [salesLoading, setSalesLoading] = useState(true);
  const [salesError, setSalesError] = useState<string | null>(null);

  const reportRef = useRef(report);
  reportRef.current = report;
  const salesRef = useRef(sales);
  salesRef.current = sales;

  const audience = useMemo(() => {
    if (canUseUserFilter && userFilter === 'All') {
      return { mode: 'team' as const, label: 'the sales team (All users)' };
    }
    if (!canUseUserFilter || userFilter === 'Me' || !userFilter) {
      return { mode: 'individual' as const, label: ownerLabel(username) || username };
    }
    return {
      mode: 'individual' as const,
      label: ownerLabel(String(userFilter)) || String(userFilter),
    };
  }, [canUseUserFilter, userFilter, ownerLabel, username]);

  const audienceRef = useRef(audience);
  audienceRef.current = audience;

  const requestCodes = useMemo((): string[] | null => {
    if (!canUseUserFilter) return actorSalespersonCodes;
    if (userFilter === 'All') return null;
    if (userFilter === 'Me') return actorSalespersonCodes;
    const key = String(userFilter).trim().toLowerCase();
    const fromMap = Object.entries(salespersonCodesByUsername).find(
      ([u]) => u.trim().toLowerCase() === key,
    );
    if (fromMap) return fromMap[1] ?? [];
    if (key === username.trim().toLowerCase()) return actorSalespersonCodes;
    return [];
  }, [
    canUseUserFilter,
    userFilter,
    actorSalespersonCodes,
    salespersonCodesByUsername,
    username,
  ]);

  const requestCodesKey =
    requestCodes === null
      ? `__all__:${canUseUserFilter ? '1' : '0'}`
      : requestCodes.map((c) => c.trim().toUpperCase()).sort().join(',');

  // When week / User filter changes, drop the previous Gemini narrative so the local
  // scorecard text shows until the user presses Refresh again.
  const scopeKey = `${report.weekStart}|${audience.mode}|${audience.label}|${requestCodesKey}`;
  const scopeRef = useRef(scopeKey);
  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    setAi(null);
    setLastRefreshedAt(null);
  }, [scopeKey]);

  useEffect(() => {
    let cancelled = false;
    setSalesLoading(true);
    setSalesError(null);
    const requestAll = canUseUserFilter && userFilter === 'All';
    void fetchDashboardMonthlySales(requestCodes, { requestAll })
      .then((result) => {
        if (!cancelled) setSales(result);
      })
      .catch((e) => {
        if (!cancelled) {
          setSales(null);
          setSalesError(e instanceof Error ? e.message : 'Failed to load sales.');
        }
      })
      .finally(() => {
        if (!cancelled) setSalesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCodesKey, canUseUserFilter, userFilter]);

  const runAiRefresh = useCallback(async () => {
    setAiLoading(true);
    try {
      // Wait for sales if still loading so Gemini gets the chart numbers.
      let salesSnapshot = salesRef.current;
      if (salesLoading && !salesSnapshot) {
        try {
          const requestAll = canUseUserFilter && userFilter === 'All';
          salesSnapshot = await fetchDashboardMonthlySales(requestCodes, { requestAll });
          setSales(salesSnapshot);
          setSalesError(null);
        } catch (e) {
          setSalesError(e instanceof Error ? e.message : 'Failed to load sales.');
        } finally {
          setSalesLoading(false);
        }
      }
      const result = await fetchCrmWeeklyAiNarrative(
        reportRef.current,
        audienceRef.current,
        salesSnapshot,
      );
      setAi(result);
      setLastRefreshedAt(new Date().toISOString());
    } finally {
      setAiLoading(false);
    }
  }, [salesLoading, canUseUserFilter, userFilter, requestCodes]);

  const narrative = ai ?? buildLocalWeeklyNarrative(report);

  const filteredVisits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return report.visits;
    return report.visits.filter((row) => {
      const hay = [row.companyName, row.code, row.title, row.ownerLabel, row.description]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [report.visits, search]);

  const filteredNotVisited = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return report.notVisited;
    return report.notVisited.filter((row) => {
      const hay = [row.companyName, row.code, row.accountSalesperson].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [report.notVisited, search]);

  return (
    <div className="analytics-weekly">
      <p className="analytics-page__summary">
        Week of {report.weekLabel} · {report.visitCount} visit
        {report.visitCount === 1 ? '' : 's'} · {report.accountCount} account
        {report.accountCount === 1 ? '' : 's'} · New Visit {report.newVisitsMonth.uniqueCount}/
        {report.newVisitsMonth.target} ({report.newVisitsMonth.monthLabel})
      </p>

      <Scorecard report={report} />

      <section className="card analytics-weekly__narrative">
        <header className="analytics-weekly__report-head">
          <div className="analytics-weekly__report-head-row">
            <div>
              <h2 className="analytics-weekly__report-title">Summary — CRM Weekly Visit Report</h2>
              <p className="analytics-weekly__report-date">
                Report week: {report.weekLabel}
                {audience.mode === 'individual' ? ` · For ${audience.label}` : ' · Team view (All)'}
              </p>
            </div>
            <div className="analytics-weekly__refresh">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={aiLoading || salesLoading}
                onClick={() => void runAiRefresh()}
              >
                {aiLoading ? 'Refreshing…' : 'Refresh summary'}
              </button>
              <p className="analytics-weekly__refresh-meta">
                {lastRefreshedAt
                  ? `Last refreshed ${formatSingaporeDateTime(lastRefreshedAt)}`
                  : aiLoading
                    ? 'Refreshing…'
                    : 'Not refreshed yet'}
              </p>
            </div>
          </div>
        </header>
        {aiLoading ? (
          <p className="analytics-weekly__ai-note">Generating Gemini summary…</p>
        ) : null}
        <h3 className="analytics-weekly__section-title">Summary</h3>
        <p className="analytics-weekly__prose">{narrative.summary}</p>
        <h3 className="analytics-weekly__section-title">Recommendation</h3>
        <p className="analytics-weekly__prose">{narrative.recommendation}</p>
        <h3 className="analytics-weekly__section-title">Key findings</h3>
        <ul className="analytics-weekly__findings">
          {narrative.findings.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {narrative.activityBrief ? (
          <>
            <h3 className="analytics-weekly__section-title">What they did</h3>
            <p className="analytics-weekly__prose">{narrative.activityBrief}</p>
          </>
        ) : null}
        {narrative.salespersonBrief ? (
          <>
            <h3 className="analytics-weekly__section-title">Salesperson performance</h3>
            <p className="analytics-weekly__prose">{narrative.salespersonBrief}</p>
          </>
        ) : null}
        <p className="analytics-weekly__ai-note">
          {narrative.source === 'gemini'
            ? 'Written by Gemini from visits, projects, update cadence, duties and sales. Press Refresh summary to regenerate.'
            : narrative.source === 'fallback'
              ? `Showing local numbers — Gemini could not run${narrative.geminiError ? ` (${narrative.geminiError})` : ''}. Upload crm-weekly-summary-proxy.php with the rest of dist/, and keep GEMINI_API_KEY in order-email-secrets.local.php on the host.`
              : 'Showing local summary. Press Refresh summary to generate a Gemini rewrite (does not auto-refresh).'}
        </p>
      </section>

      <div className="analytics-weekly__charts analytics-weekly__charts--two">
        <DayBarChart report={report} />
        <TitleMixBars report={report} />
      </div>

      <SalesTrendCard data={sales} loading={salesLoading} error={salesError} />

      <section className="card analytics-weekly__projects">
        <h3 className="analytics-weekly__section-title">Project pipeline</h3>
        <p className="analytics-weekly__prose">
          {report.projects.total} project{report.projects.total === 1 ? '' : 's'}:{' '}
          <strong>{report.projects.ongoing}</strong> ongoing ·{' '}
          <strong>{report.projects.onHold}</strong> on hold ·{' '}
          <strong>{report.projects.completed}</strong> completed.
        </p>
        {report.projects.problemProjects.length > 0 ? (
          <ul className="analytics-weekly__findings">
            {report.projects.problemProjects.map((p) => (
              <li key={p.id}>
                <strong>{p.title}</strong> — {p.companyName} ({p.status})
              </li>
            ))}
          </ul>
        ) : (
          <p className="analytics-weekly__empty">No On Hold projects in this scope.</p>
        )}
        <h3 className="analytics-weekly__section-title">Project update cadence</h3>
        <p className="analytics-weekly__prose">
          Ongoing projects should receive a human update within {PROJECT_UPDATE_ON_TIME_DAYS} days.{' '}
          <strong>{report.projectUpdateHealth.onTime}</strong> on time ·{' '}
          <strong>{report.projectUpdateHealth.stale}</strong> stale ·{' '}
          <strong>{report.projectUpdateHealth.neverUpdated}</strong> never updated.
        </p>
        {report.projectUpdateHealth.staleProjects.length > 0 ? (
          <ul className="analytics-weekly__findings">
            {report.projectUpdateHealth.staleProjects.map((p) => (
              <li key={p.id}>
                <strong>{p.title}</strong> — {p.companyName}
                {p.daysSinceUpdate == null
                  ? ' · no human update yet'
                  : ` · last update ${p.daysSinceUpdate} day${p.daysSinceUpdate === 1 ? '' : 's'} ago`}
              </li>
            ))}
          </ul>
        ) : (
          <p className="analytics-weekly__empty">All ongoing projects are within the update window.</p>
        )}
      </section>

      {report.dutyFollowUps.withDuty > 0 ? (
        <section className="card analytics-weekly__themes">
          <h3 className="analytics-weekly__section-title">Assigned duties and follow-up</h3>
          <p className="analytics-weekly__prose">
            {report.dutyFollowUps.withDuty} visit description
            {report.dutyFollowUps.withDuty === 1 ? '' : 's'} mention an assigned duty or next action ·{' '}
            <strong>{report.dutyFollowUps.followedUp}</strong> followed up later ·{' '}
            <strong>{report.dutyFollowUps.openCount}</strong> still open.
          </p>
          <ul className="analytics-weekly__findings">
            {report.dutyFollowUps.items
              .filter((item) => !item.followedUp)
              .slice(0, 12)
              .map((item) => (
                <li key={item.taskId}>
                  <button type="button" className="analytics-weekly__inline-link" onClick={() => onOpenTask(item.taskId)}>
                    {item.companyName}
                  </button>{' '}
                  ({item.dayLabel} · {item.ownerLabel}) — open: “{item.snippet}”
                </li>
              ))}
          </ul>
          {report.dutyFollowUps.openCount === 0 ? (
            <p className="analytics-weekly__empty">All assigned duties in this week’s visits have a later follow-up.</p>
          ) : null}
        </section>
      ) : null}

      {report.highFrequency.length > 0 ? (
        <section className="card analytics-weekly__themes">
          <h3 className="analytics-weekly__section-title">
            High-frequency accounts (≥{HIGH_FREQ_VISIT_THRESHOLD} visits this week)
          </h3>
          <ul className="analytics-weekly__gap-list">
            {report.highFrequency.slice(0, 20).map((row) => (
              <li key={row.clientId}>
                <button
                  type="button"
                  className="analytics-weekly__gap-row"
                  onClick={() => onOpenAccount(row.clientId)}
                >
                  <span className="analytics-weekly__visit-name">{row.companyName}</span>
                  <span className="analytics-weekly__visit-meta mono">
                    {row.code} · {row.visitCount} visits
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {report.byOwner.length > 0 ? (
        <section className="card analytics-weekly__owners">
          <h3 className="analytics-weekly__section-title">Salesperson performance</h3>
          <p className="analytics-weekly__chart-meta">
            New Visit {report.newVisitsMonth.target} and daily {report.dailyTarget} are personal targets —
            each person must hit their own; extras do not cover others.
          </p>
          <div className="analytics-weekly__owner-head analytics-weekly__owner-head--targets" role="row">
            <span>Person</span>
            <span>Visits</span>
            <span>Accounts</span>
            <span>New Visit</span>
            <span>Daily 8</span>
            <span>Main task types</span>
          </div>
          <ul className="analytics-weekly__owner-list">
            {report.byOwner.map((row) => {
              const key = row.ownerId || row.ownerLabel;
              return (
                <li key={key} className="analytics-weekly__owner-row analytics-weekly__owner-row--targets">
                  <span>{row.ownerLabel}</span>
                  <span className="mono">{row.visitCount}</span>
                  <span className="mono">{row.accountCount}</span>
                  <span
                    className={`mono${row.newVisitMet ? ' analytics-weekly__target-met' : ' analytics-weekly__target-miss'}`}
                    title={
                      row.newVisitMet
                        ? 'Personal New Visit target met'
                        : `${row.newVisitRemaining} more unique New Visit accounts needed`
                    }
                  >
                    {row.newVisitUnique}/{row.newVisitTarget}
                  </span>
                  <span
                    className={`mono${row.dailyTargetMet ? ' analytics-weekly__target-met' : ' analytics-weekly__target-miss'}`}
                    title={
                      row.dailyTargetMet
                        ? 'Met 8 visits every weekday this week'
                        : `${row.workdaysMet} of ${row.workdayCount} weekdays met personal daily target`
                    }
                  >
                    {row.workdaysMet}/{row.workdayCount}
                  </span>
                  <OwnerTitleHint report={report} ownerKey={key} />
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="card analytics-weekly__visits-panel">
        <header className="analytics-weekly__panel-head">
          <h3 className="analytics-weekly__section-title">This week’s visits</h3>
          <p className="analytics-weekly__chart-meta">{filteredVisits.length} shown</p>
        </header>
        {filteredVisits.length === 0 ? (
          <p className="analytics-weekly__empty">No visits match this filter.</p>
        ) : (
          <ul className="analytics-weekly__visit-list">
            {filteredVisits.map((row) => (
              <VisitRowButton
                key={row.taskId}
                row={row}
                onOpenTask={onOpenTask}
                onOpenAccount={onOpenAccount}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="card analytics-weekly__gap-panel">
        <header className="analytics-weekly__panel-head">
          <h3 className="analytics-weekly__section-title">Not visited (last 3 months)</h3>
          <p className="analytics-weekly__chart-meta">{filteredNotVisited.length} account(s)</p>
        </header>
        {filteredNotVisited.length === 0 ? (
          <p className="analytics-weekly__empty">
            Every account in scope has at least one task in the rolling 3-month window.
          </p>
        ) : (
          <ul className="analytics-weekly__gap-list">
            {filteredNotVisited.slice(0, 40).map((row) => (
              <li key={row.clientId}>
                <button
                  type="button"
                  className="analytics-weekly__gap-row"
                  onClick={() => onOpenAccount(row.clientId)}
                >
                  <span className="analytics-weekly__visit-name">{row.companyName}</span>
                  <span className="analytics-weekly__visit-meta mono">
                    {row.code} · {row.accountSalesperson}
                    {row.lastTaskMonthKey
                      ? ` · last ${formatMonthKeyLabel(row.lastTaskMonthKey)}`
                      : ' · never'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {filteredNotVisited.length > 40 ? (
          <p className="analytics-weekly__ai-note">
            Showing first 40 of {filteredNotVisited.length}. Use Coverage tab for the full list.
          </p>
        ) : null}
      </section>
    </div>
  );
}
